import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  type BoxProps,
  type SxProps,
} from "@mui/material";

type HeaderAddonProps = {
  addon: React.ReactNode;
  testId?: string;
  sx?: SxProps;
} & BoxProps;

const HeaderAddon = observer(function HeaderAddon({ addon, testId = "", sx }: HeaderAddonProps) {
  return (
    <Box
      sx={[
        {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          paddingLeft: 2,
          marginRight: -1,
          borderLeft: addon ? 1 : 0,
          borderColor: addon ? "grey.200" : undefined,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      key={testId}
    >
      <Typography
        color="secondary"
        variant="labelLarge"
        data-testid={`${testId}__subtitle`}
        key={testId}
      >
        {addon}
      </Typography>
    </Box>
  );
});

type TitleAddonProps = {
  addon: React.ReactNode | React.ReactNode[];
  testId: string;
  sx?: SxProps;
};
const TitleAddon = observer(function TitleAddon({ addon, testId, sx }: TitleAddonProps) {
  return Array.isArray(addon) ? (
    addon.map((addonI, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <HeaderAddon key={`${testId}-addon-${index}`} addon={addonI} testId={testId} />
    ))
  ) : (
    <HeaderAddon
      addon={addon}
      testId={testId}
      sx={[{ borderLeft: "none" }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    />
  );
});

export type BaseCardProps = {
  title?: React.ReactNode;
  titleAddon?: React.ReactNode | React.ReactNode[];
  children?: React.ReactNode;
  loading?: boolean;
  error?: boolean;
  errorMessage?: string | null;
  testId?: string;
  height?: string | number;
  minHeight?: number;
  sx?: SxProps;
  size?: "small" | "medium";
  isCardRounded?: boolean;
  orientation?: "horizontal" | "vertical";
};
/**
 * - This component is used as the base for all other cards.
 * - It is used to display a title, as well as any children passed to it.
 * - In addition to the data, the component also displays a loading indicator and an error message.
 * */
const BaseCard = observer(function BaseCard({
  title = "",
  titleAddon = [],
  children,
  loading = false,
  error = false,
  errorMessage = "Unable to load data",
  testId = "",
  height = "inherit",
  minHeight = 0,
  sx = {},
  size = "medium",
  isCardRounded = false,
  orientation = "horizontal",
}: BaseCardProps) {
  const renderComponents = () => {
    if (error) {
      return (
        <Typography variant="bodyMedium" color="error">
          {errorMessage}
        </Typography>
      );
    }
    return loading ? <CircularProgress color="primary" size="3.5rem" /> : children;
  };

  const isVertical = orientation === "vertical";
  const headerSize = size === "small" ? 32 : 48;

  return (
    <Paper
      elevation={1}
      sx={[
        {
          height: "100%",
          minHeight,
          borderRadius: isCardRounded && "16px",
          backgroundColor: "white",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        flexGrow={1}
        height={height}
        minHeight={minHeight}
        sx={{ borderRadius: isCardRounded && "16px" }}
      >
        <Grid
          style={{ height: "inherit" }}
          container
          direction={isVertical ? "row" : "column"}
          justifyContent="flex-start"
          alignItems="stretch"
        >
          <Grid
            item
            maxWidth="100%"
            component={Paper}
            elevation={1}
            sx={{
              borderRadius: isCardRounded && "16px",
              boxShadow: isCardRounded && "none",
              backgroundColor: "white",
              ...(isVertical && {
                width: headerSize,
                minWidth: headerSize,
                maxWidth: headerSize,
              }),
            }}
          >
            <Box
              px={isVertical ? 1 : 2}
              py={isVertical ? 2 : 0}
              flexGrow={1}
              height={isVertical ? "100%" : headerSize}
              width={isVertical ? headerSize : "100%"}
              display="flex"
              flexDirection={isVertical ? "column" : "row"}
              alignItems="center"
              justifyContent={isVertical ? "center" : "flex-start"}
            >
              <Typography
                variant="titleSmall"
                sx={{
                  flexGrow: 1,
                  textTransform: "capitalize",
                  ...(isVertical && {
                    textOrientation: "sideways",
                    writingMode: "vertical-lr",
                    whiteSpace: "nowrap",
                    width: "max-content",
                    transform: "rotate(180deg)",
                    // Center
                    textAlign: "center",
                  }),
                }}
                data-testid={`${testId}__title`}
              >
                {title}
              </Typography>

              {titleAddon && !isVertical ? <TitleAddon addon={titleAddon} testId={testId} /> : null}
            </Box>
          </Grid>
          <Grid item xs sx={{ maxWidth: "100% !important", height: "100%" }}>
            <Box
              flexGrow={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height={"calc(100%)"}
              width={"100%"}
              data-testid={`${testId}__body`}
              p={1}
            >
              {renderComponents()}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
});

export default BaseCard;
